<template>
    <Widget :minWidth="150" :minHeight="200" :maxWidth="250" :maxHeight="600" :title="title" v-show="!initializing">
        <SceneList v-bind:scenes="scenes" @onEdit="onEdit" @onSelected="onSelected" @onMoveAll="onMoveAll" />
        <Button @click="onAdd">Add</Button>
        <SceneEditorWindow
            ref="editor"
            @onChange="onSceneSaved"
            @onAdd="onSceneAdded"
            @onCancel="showEditor = false"
            @onDelete="onSceneDeleted"
            v-show="showEditor"
        />
    </Widget>
</template>

<script lang="ts">
import { mapActions, mapState } from 'vuex';
import * as jsonpatch from 'fast-json-patch';
import _ from 'lodash';

import SceneList from './SceneList.vue';
import Widget from '@/components/common/widget/Widget.vue';
import SceneEditorWindow from '@/components/scene/EditorWindow.vue';
import { COLLECTION_TYPES } from '@/core/collections/constants';

export default {
    components: {
        SceneList: SceneList,
        Widget: Widget,
        SceneEditorWindow: SceneEditorWindow,
    },
    data: function() {
        return {
            showEditor: false,
            title: 'Scene Library',
            initializing: true,
        };
    },
    computed: {
        ...mapState('collections', {
            scenes: 'scenes',
            players: 'players',
        }),
        ...mapState('live', {
            campaignId: 'sessionId',
            userId: 'userId',
            connected: 'connected',
        }),
        ...mapState('gamestate', {
            activeScene: 'activeScene',
        }),
        player: function() {
            if (!this.players) {
                return null;
            }
            return Object.values(this.players).find(p => p['userId'] === this.userId);
        },
    },
    watch: {
        campaignId: async function() {
            await this.setUpPlayer();
        },
        userId: async function() {
            await this.setUpPlayer();
        },
        connected: async function() {
            await this.setUpPlayer();
        },
        players: {
            handler() {
                this.trackScene();
            },
            deep: true,
        },
        scenes: function() {
            this.trackScene();
        },
        activeScene: function() {
            this.trackScene();
        },
    },
    mounted: async function() {
        await this.setUpPlayer();
    },
    methods: {
        ...mapActions('collections', {
            loadScenes: 'loadScenes',
            updateScene: 'update',
            addScene: 'add',
            removeScene: 'remove',
        }),
        ...mapActions('players', {
            loadPlayers: 'load',
            addPlayer: 'add',
            updatePlayer: 'update',
        }),
        setUpPlayer: async function() {
            if (this.connected && this.userId && this.campaignId && !this.player) {
                const players = await this.loadPlayers();
                const player = Object.values(players).find(p => p['userId'] === this.userId);
                if (!player) {
                    const p = { userId: this.userId };
                    await this.addPlayer({ player: p });
                } else {
                    this.trackScene();
                }
            }
        },
        trackScene: function() {
            if (this.player && this.activeScene && this.scenes) {
                if (this.player.sceneId != this.activeScene._id) {
                    const scene = this.scenes[this.player.sceneId];
                    if (scene) {
                        this.$store.dispatch('gamestate/activateScene', scene);
                    }
                }
                this.initializing = false;
            }
        },
        onAdd: function() {
            this.showEditor = true;
            const scene = {
                name: 'New Scene',
                scene: { stage: '.' },
                stage: {
                    grid: { type: 'square', size: 50 },
                    bounds: {
                        nw: { q: 0, r: 0 },
                        se: { q: 20, r: 20 },
                    },
                    color: '#223344',
                    elements: [
                        {
                            id: 'background',
                            asset: { kind: 'image', src: '/static/assets/hideout.png' },
                            pos: { q: 0, r: 0, pa: '00' },
                        },
                    ],
                },
            };
            this.$refs.editor.saveButton = 'Add';
            this.$refs.editor.showDelete = false;
            this.$refs.editor.scene = scene;
        },
        onEdit: function({ scene }) {
            this.showEditor = true;
            this.$refs.editor.saveButton = 'Save';
            this.$refs.editor.showDelete = true;
            this.$refs.editor.scene = scene;
        },
        onSelected: function({ scene }) {
            this.notifyPlayerMove(scene._id, this.player);
        },
        notifyPlayerMove: function(sceneId, player) {
            const modifiedPlayer = _.cloneDeep(player);
            modifiedPlayer.sceneId = sceneId;
            const patch = jsonpatch.compare(player, modifiedPlayer);
            if (patch.length > 0) {
                this.updatePlayer({ id: player._id, patch });
            }
        },
        onMoveAll: function({ scene }) {
            Object.values(this.players).forEach(player => {
                this.notifyPlayerMove(scene._id, player);
            });
        },
        onSceneAdded({ scene }) {
            this.showEditor = false;
            this.addScene({ collection: COLLECTION_TYPES.scenes, item: scene });
        },
        onSceneSaved({ id, patch }) {
            this.showEditor = false;
            this.updateScene({ collection: COLLECTION_TYPES.scenes, id, patch });
        },
        onSceneDeleted({ sceneId }) {
            this.showEditor = false;
            this.removeScene({ collection: COLLECTION_TYPES.scenes, id: sceneId });
        },
    },
};
</script>
